import React, { useEffect, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { WpDialog, Field, SelectInput, CheckInput } from './DialogShell';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { renderObjectsToCanvas, canvasToBlob, downloadBlob, safeFileName } from '../../lib/renderUtils';
import { createBuildingBlock, listBuildingBlocks, deleteBuildingBlock, apiErrorMessage } from '../../lib/projectApi';
import { Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

/** Save selected object(s) as PNG / JPEG picture. props: { objectIds } */
export const SaveAsPictureDialog = ({ props }) => {
  const { closeDialog, getActiveObjects, state } = useEditor();
  const objects = getActiveObjects().filter((o) => (props.objectIds || []).includes(o.id));
  const [format, setFormat] = useState('png');
  const [scale, setScale] = useState(2);
  const [transparent, setTransparent] = useState(true);
  const [name, setName] = useState(objects.length === 1 ? (objects[0].properties?.altText || objects[0].type) : 'selection');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!objects.length) return undefined;
    renderObjectsToCanvas(objects, { scale: 1, transparent: format === 'png' && transparent, textFlow: state.textFlow }).then((c) => alive && setPreview(c.toDataURL('image/png'))).catch(() => alive && setPreview(null));
    return () => { alive = false; };
    // `objects` is looked up from props.objectIds (listed); the document / its text flow cannot change while this modal
    // dialog is open, so the preview is keyed on the export options only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [format, transparent, props.objectIds]);

  const save = async () => {
    setBusy(true);
    try {
      const canvas = await renderObjectsToCanvas(objects, { scale, transparent: format === 'png' && transparent, textFlow: state.textFlow });
      const blob = await canvasToBlob(canvas, format === 'jpg' ? 'image/jpeg' : 'image/png', 0.92);
      downloadBlob(blob, `${safeFileName(name)}.${format}`);
      toast.success('Picture saved');
      closeDialog();
    } catch (e) { console.error(e); toast.error('Could not render the picture. External images may block export.'); }
    finally { setBusy(false); }
  };

  return (
    <WpDialog name="save-as-picture" title="Save as Picture" width="w-[560px]" onOk={save} okLabel={busy ? 'Saving…' : 'Save'} okDisabled={busy || !objects.length} onCancel={closeDialog}>
      {!objects.length ? <p className="text-muted-foreground">Select one or more objects first.</p> : (
        <div className="grid grid-cols-[1fr_200px] gap-5">
          <div className="space-y-3">
            <Field label="File name"><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="save-picture-name" /></Field>
            <Field label="Save as type"><SelectInput value={format} onChange={setFormat} options={[{ value: 'png', label: 'PNG Portable Network Graphics (*.png)' }, { value: 'jpg', label: 'JPEG File Interchange Format (*.jpg)' }]} className="w-full" testId="save-picture-format" /></Field>
            <Field label="Resolution"><SelectInput value={String(scale)} onChange={(v) => setScale(Number(v))} options={[{ value: '1', label: 'Web (96 dpi)' }, { value: '2', label: 'High quality (192 dpi)' }, { value: '3', label: 'Commercial print (288 dpi)' }]} className="w-full" testId="save-picture-resolution" /></Field>
            {format === 'png' && <CheckInput checked={transparent} onChange={setTransparent} label="Transparent background" testId="save-picture-transparent" />}
            <p className="text-xs text-muted-foreground">{objects.length} object(s) selected.</p>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Preview</div>
            <div className="h-[170px] rounded-md border border-border wp-checker flex items-center justify-center overflow-hidden" data-testid="save-picture-preview">{preview ? <img src={preview} alt="Preview" className="max-h-full max-w-full object-contain" /> : <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}</div>
          </div>
        </div>
      )}
    </WpDialog>
  );
};

/** Save as / manage Building Blocks. props: { objectIds, manage } */
export const BuildingBlockDialog = ({ props }) => {
  const { closeDialog, getActiveObjects, state } = useEditor();
  const objects = getActiveObjects().filter((o) => (props.objectIds || []).includes(o.id));
  const [name, setName] = useState(objects.length === 1 ? `${objects[0].type} block` : 'My block');
  const [category, setCategory] = useState(props.category || 'Page Parts');
  const [busy, setBusy] = useState(false);
  const [blocks, setBlocks] = useState(null);
  const manage = !!props.manage || !objects.length;

  useEffect(() => { if (manage) listBuildingBlocks().then(setBlocks).catch(() => setBlocks([])); }, [manage]);

  const save = async () => {
    if (!name.trim()) { toast.error('Please enter a name.'); return; }
    setBusy(true);
    try {
      let obj;
      if (objects.length === 1) obj = objects[0];
      else {
        const minX = Math.min(...objects.map((o) => o.x)), minY = Math.min(...objects.map((o) => o.y));
        const maxX = Math.max(...objects.map((o) => o.x + o.width)), maxY = Math.max(...objects.map((o) => o.y + o.height));
        obj = { type: 'group', x: minX, y: minY, width: maxX - minX, height: maxY - minY, rotation: 0, properties: {}, children: objects.map((o, i) => ({ ...o, x: o.x - minX, y: o.y - minY, layer: i })) };
      }
      let thumbnail = null;
      try { const c = await renderObjectsToCanvas(objects, { scale: 0.5, transparent: true, textFlow: state.textFlow }); thumbnail = c.toDataURL('image/png'); } catch { /* optional */ }
      const { id, layer, ...rest } = obj;
      await createBuildingBlock({ name: name.trim(), category, object: rest, thumbnail });
      window.dispatchEvent(new CustomEvent('wp:building-blocks-changed'));
      toast.success(`Saved “${name.trim()}” to ${category}`, { description: `Find it under Insert ▸ ${category}.` });
      closeDialog();
    } catch (e) { toast.error(await apiErrorMessage(e, 'Could not save the building block.')); }
    finally { setBusy(false); }
  };
  const remove = async (b) => { try { await deleteBuildingBlock(b.id); setBlocks((l) => l.filter((x) => x.id !== b.id)); toast.success('Building block deleted'); } catch (e) { toast.error(await apiErrorMessage(e)); } };

  if (manage) {
    return (
      <WpDialog name="building-block" title="Building Block Library" width="w-[600px]" onCancel={closeDialog} cancelLabel="Close">
        {blocks === null && <div className="text-muted-foreground">Loading…</div>}
        {blocks && blocks.length === 0 && <div className="rounded-md border border-dashed border-border p-6 text-center text-muted-foreground" data-testid="building-block-empty">No building blocks saved yet. Select an object, right-click and choose “Save as Building Block…”.</div>}
        {blocks && blocks.length > 0 && (
          <div className="max-h-[320px] overflow-y-auto divide-y divide-border" data-testid="building-block-list">
            {blocks.map((b) => (
              <div key={b.id} className="flex items-center gap-3 py-2">
                <div className="h-12 w-16 rounded-sm border border-border bg-white flex items-center justify-center overflow-hidden">{b.thumbnail && <img src={b.thumbnail} alt="" className="max-h-full max-w-full object-contain" />}</div>
                <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{b.name}</div><div className="text-xs text-muted-foreground">{b.category}</div></div>
                <Button variant="ghost" size="sm" onClick={() => remove(b)} className="text-muted-foreground hover:text-destructive" data-testid={`building-block-delete-${b.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </WpDialog>
    );
  }

  return (
    <WpDialog name="building-block" title="Create New Building Block" width="w-[480px]" onOk={save} okLabel={busy ? 'Saving…' : 'OK'} okDisabled={busy} onCancel={closeDialog}>
      <div className="space-y-3">
        <Field label="Title"><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="building-block-name" /></Field>
        <Field label="Gallery"><SelectInput value={category} onChange={setCategory} options={['Page Parts', 'Calendars', 'Borders & Accents', 'Advertisements', 'My Building Blocks']} className="w-full" testId="building-block-category" /></Field>
        <p className="text-xs text-muted-foreground">{objects.length} object(s) will be saved so you can reuse them from the Insert tab.</p>
      </div>
    </WpDialog>
  );
};
